"use client";

/**
 * Ghost-text overlay (B-117, 2026-06-18).
 *
 * Renders a transparent `userValue` span followed by the muted-gray
 * ghost suffix, absolutely positioned to sit BEHIND the existing
 * <input>. The host element must be `position: relative`.
 *
 * Caller is responsible for matching the input's font and padding —
 * the GhostLayer just paints text inside the element it lives in.
 * Pass `inputStyle` for any specific overrides (font-size, padding)
 * when the host doesn't propagate them via `font: inherit`.
 *
 * The layer NEVER captures events — it sits at z-index 0 behind a
 * transparent `<input>` and `pointer-events: none` on the wrapper
 * so the caret always lives on the real input.
 */

import type { CSSProperties } from "react";

interface Props {
  /** Current value the user has typed. */
  userValue: string;
  /** The suffix to render in muted gray. Empty string = invisible. */
  suffix: string;
  /** Optional extra style overrides to match the host input. */
  inputStyle?: CSSProperties;
  /** Class name forwarded to the wrapper. */
  className?: string;
}

export function GhostLayer({
  userValue,
  suffix,
  inputStyle,
  className,
}: Props) {
  if (!suffix) return null;
  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        whiteSpace: "pre",
        overflow: "hidden",
        pointerEvents: "none",
        fontFamily: "inherit",
        color: "transparent",
        ...inputStyle,
      }}
    >
      <span style={{ color: "transparent" }}>{userValue}</span>
      <span style={{ color: "rgba(237, 237, 237, 0.32)" }}>{suffix}</span>
    </span>
  );
}
